import React from 'react';
import { ArrowLeft, Shield, FileText, AlertTriangle, Brain, CreditCard } from 'lucide-react';

const LegalFooter = ({ onNavigate }) => (
  <footer className="py-6 px-4 bg-gray-100 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
    <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
      <button onClick={() => onNavigate('terms-of-service')} className="hover:text-[#FF6600] transition-colors" data-testid="legal-footer-terms">Terms of Service</button>
      <span>|</span>
      <button onClick={() => onNavigate('privacy-policy')} className="hover:text-[#FF6600] transition-colors" data-testid="legal-footer-privacy">Privacy Policy</button>
      <span>|</span>
      <button onClick={() => onNavigate('health-disclaimer')} className="hover:text-[#FF6600] transition-colors" data-testid="legal-footer-health">Health Disclaimer</button>
      <span>|</span>
      <button onClick={() => onNavigate('ai-disclaimer')} className="hover:text-[#FF6600] transition-colors" data-testid="legal-footer-ai">AI Disclaimer</button>
    </div>
  </footer>
);

const PageWrapper = ({ title, icon: Icon, children, onBack }) => (
  <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
    <div className="bg-black py-3 px-4">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-v4.png" alt="Logo" className="h-12 w-12 object-contain" />
          <span className="text-white font-semibold text-lg hidden sm:block">Nutrifitness AI</span>
        </div>
        {onBack && (
          <button onClick={onBack} className="flex items-center gap-2 text-white hover:text-[#FF6600] transition-colors text-sm" data-testid="legal-back-btn">
            <ArrowLeft size={18} /> Back
          </button>
        )}
      </div>
    </div>
    <div className="flex-1 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#FF6600]/10 rounded-xl flex items-center justify-center">
            <Icon size={24} className="text-[#FF6600]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{title}</h1>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-10">
          {children}
        </div>
      </div>
    </div>
  </div>
);

const sectionTitle = "text-xl font-bold text-gray-900 dark:text-white mb-3 mt-8 first:mt-0";
const paragraph = "text-gray-600 dark:text-gray-300 leading-relaxed mb-4";
const listItem = "text-gray-600 dark:text-gray-300 leading-relaxed";

const TermsOfService = ({ onBack }) => (
  <PageWrapper title="Terms of Service" icon={FileText} onBack={onBack}>
    <p className="text-gray-500 dark:text-gray-400 mb-6 font-medium">Effective Date: March 26, 2026</p>

    <h2 className={sectionTitle}>1. Platform Coverage</h2>
    <p className={paragraph}>Nutrifitness AI is accessible via website (Nutrifitness.us) and mobile applications (iOS and Android). These Terms apply to all users across all platforms.</p>

    <h2 className={sectionTitle}>2. Services</h2>
    <p className={paragraph}>Nutrifitness AI provides fitness videos, AI-generated fitness and nutrition suggestions, and general recipes (vegan, vegetarian, gluten-free, organic). All content is for informational purposes only.</p>

    <h2 className={sectionTitle}>3. No Medical or Nutritional Advice</h2>
    <p className={paragraph}>Nutrifitness AI is not a licensed medical provider or dietitian. We do NOT provide diagnosis, treatment, or diet prescriptions. Consult a licensed professional before making health decisions.</p>

    <h2 className={sectionTitle}>4. AI-Generated Content</h2>
    <p className={paragraph}>AI content may be inaccurate or incomplete. Do not rely on AI for medical, nutrition, or fitness advice. Use at your own risk.</p>

    <h2 className={sectionTitle}>5. Assumption of Risk</h2>
    <p className={paragraph}>Users voluntarily assume all risks associated with fitness activities, AI recommendations, and dietary suggestions.</p>

    <h2 className={sectionTitle}>6. Recipes & Food Content</h2>
    <p className={paragraph}>All recipes and nutrition content are for general informational purposes. Users are responsible for checking allergies, dietary restrictions, or medical conditions.</p>

    <h2 className={sectionTitle}>7. Subscriptions</h2>
    <p className={paragraph}>7-day free trial is offered. Paid subscriptions (monthly, annual, premium) renew automatically unless canceled. See Cancellation & Refund Policy below.</p>

    <h2 className={sectionTitle}>8. Cancellation & Refund Policy</h2>
    <p className={paragraph}>Free trials must be canceled before the trial ends to avoid payment. Paid subscriptions can request refunds within 7 days of payment. Refund requests beyond this period may be declined. Users acknowledge that Nutrifitness AI is not responsible for chargebacks after acceptance.</p>

    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl p-5 my-6">
      <h3 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2"><CreditCard size={18} className="text-[#FF6600]" /> Subscription Pricing</h3>
      <ul className="space-y-2 text-gray-700 dark:text-gray-300">
        <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF6600] rounded-full"></span> Monthly: $19.99/month</li>
        <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF6600] rounded-full"></span> Annual: $89.99/year</li>
        <li className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF6600] rounded-full"></span> Premium: $49.99/month</li>
      </ul>
    </div>

    <h2 className={sectionTitle}>9. Limitation of Liability</h2>
    <p className={paragraph}>Nutrifitness AI is not liable for injuries, health complications, allergic reactions, or damages resulting from use of the platform.</p>

    <h2 className={sectionTitle}>10. App Store Disclaimer</h2>
    <p className={paragraph}>Apple is not responsible for the app or its content. Google Play and other platforms are not responsible for distribution issues.</p>

    <h2 className={sectionTitle}>11. Termination</h2>
    <p className={paragraph}>We may suspend or terminate accounts at any time for misuse.</p>

    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
      <p className="text-sm text-gray-500 dark:text-gray-400">For questions about these terms, contact us at <a href="mailto:support@nutrifitness.us" className="text-[#FF6600] hover:underline">support@nutrifitness.us</a></p>
    </div>
  </PageWrapper>
);

const PrivacyPolicy = ({ onBack }) => (
  <PageWrapper title="Privacy Policy" icon={Shield} onBack={onBack}>
    <p className="text-gray-500 dark:text-gray-400 mb-6 font-medium">Effective Date: March 26, 2026</p>

    <h2 className={sectionTitle}>1. Information We Collect</h2>
    <p className={paragraph}>We may collect name, email, usage data, device info, and subscription details.</p>

    <h2 className={sectionTitle}>2. Use of Data</h2>
    <p className={paragraph}>Data is used to operate services, improve AI, and manage subscriptions.</p>

    <h2 className={sectionTitle}>3. Data Sharing</h2>
    <p className={paragraph}>We do NOT sell personal data. We may share with service providers and payment processors.</p>

    <h2 className={sectionTitle}>4. California Rights (CCPA/CPRA)</h2>
    <p className={paragraph}>California residents may request access, deletion, or correction of personal data. Users can opt-out of sale (not applicable here as we do not sell data).</p>

    <h2 className={sectionTitle}>5. Data Retention & Security</h2>
    <p className={paragraph}>We retain data only as necessary and implement reasonable safeguards.</p>

    <h2 className={sectionTitle}>6. Children</h2>
    <p className={paragraph}>Not intended for users under 18.</p>

    <h2 className={sectionTitle}>7. Contact</h2>
    <p className={paragraph}>For privacy-related inquiries, contact us at <a href="mailto:support@nutrifitness.us" className="text-[#FF6600] hover:underline">support@nutrifitness.us</a></p>
  </PageWrapper>
);

const HealthDisclaimer = ({ onBack }) => (
  <PageWrapper title="Health & Fitness Disclaimer" icon={AlertTriangle} onBack={onBack}>
    <div className="space-y-4">
      <p className={paragraph}>Nutrifitness AI provides general fitness and nutrition information only.</p>
      <p className={paragraph}>We are not licensed medical professionals or dietitians.</p>
      <p className={paragraph}>This platform does NOT provide medical advice, diagnosis, or treatment.</p>
      <p className={paragraph}>Consult a licensed healthcare provider before starting any program. Use at your own risk.</p>
    </div>

    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-5 mt-6">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <p className="text-red-700 dark:text-red-300 text-sm font-medium">
          Always consult your doctor or qualified healthcare professional before beginning any new exercise program or making dietary changes. If you experience any discomfort or pain during exercise, stop immediately and seek medical attention.
        </p>
      </div>
    </div>
  </PageWrapper>
);

const AIDisclaimer = ({ onBack }) => (
  <PageWrapper title="AI Disclaimer" icon={Brain} onBack={onBack}>
    <div className="space-y-4">
      <p className={paragraph}>This platform uses artificial intelligence to generate recommendations.</p>
      <p className={paragraph}>AI content may contain errors, omissions, or inaccuracies.</p>
      <p className={paragraph}>Do not rely on AI as a substitute for professional medical, nutrition, or fitness advice.</p>
      <p className={paragraph}>Use AI-generated content at your own risk.</p>
    </div>

    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-5 mt-6">
      <div className="flex items-start gap-3">
        <Brain size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">
          AI-generated meal plans, workout routines, and nutritional estimates are approximations. Results may vary. Always verify critical health information with a licensed professional.
        </p>
      </div>
    </div>
  </PageWrapper>
);

const LegalPages = ({ page, onBack }) => {
  switch (page) {
    case 'terms-of-service':
    case 'terms':
      return <TermsOfService onBack={onBack} />;
    case 'privacy-policy':
    case 'privacy':
      return <PrivacyPolicy onBack={onBack} />;
    case 'health-disclaimer':
      return <HealthDisclaimer onBack={onBack} />;
    case 'ai-disclaimer':
      return <AIDisclaimer onBack={onBack} />;
    default:
      return <TermsOfService onBack={onBack} />;
  }
};

export { LegalFooter };
export default LegalPages;
